<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";
import { useAuthStore } from "@/stores/AuthStore";

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const fragment = new URLSearchParams(
  String(route.hash || "").replace(/^#/, ""),
);

const token = ref(String(fragment.get("token") || ""));
const email = ref(String(fragment.get("email") || ""));
const password = ref("");
const passwordConfirmation = ref("");
const submitting = ref(false);
const completed = ref(false);
const message = ref("");

const hasResetCredentials = computed(
  () => token.value.length > 0 && email.value.length > 0,
);

const passwordIsStrong = (value) =>
  value.length >= 12 &&
  /[a-z]/.test(value) &&
  /[A-Z]/.test(value) &&
  /\d/.test(value) &&
  /[^A-Za-z0-9]/.test(value);

const submit = async () => {
  message.value = "";

  if (!passwordIsStrong(password.value)) {
    message.value =
      "Use at least 12 characters with uppercase, lowercase, a number, and a symbol.";
    return;
  }

  if (password.value !== passwordConfirmation.value) {
    message.value = "The passwords do not match.";
    return;
  }

  submitting.value = true;
  try {
    await apiClient.get("/sanctum/csrf-cookie", {
      suppressErrorAlert: true,
    });
    const response = await apiClient.post(
      "/api/password/reset",
      {
        token: token.value,
        email: email.value,
        password: password.value,
        password_confirmation: passwordConfirmation.value,
      },
      {
        suppressErrorAlert: true,
      },
    );

    if (response.data?.signed_out) {
      auth.clearSession();
    }

    token.value = "";
    password.value = "";
    passwordConfirmation.value = "";
    completed.value = true;
    message.value = response.data?.message || "Your password has been reset.";
  } catch (error) {
    message.value =
      error.response?.data?.message ||
      "The password could not be reset. Request a new link from your administrator.";
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  // Remove reset credentials from browser history after reading them into memory.
  if (route.hash) {
    void router.replace({ name: "reset-password" });
  }
});
</script>

<template>
  <section
    class="flex min-h-screen items-center justify-center bg-gray-100 px-6 py-8 dark:bg-gray-900"
  >
    <div
      class="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:border dark:border-gray-700 dark:bg-gray-800 sm:p-8"
    >
      <img
        class="mx-auto mb-5 h-16 w-auto object-contain"
        src="/images/logo.png"
        alt="Medic 1"
      />

      <template v-if="completed">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          Password reset
        </h1>
        <p
          class="mt-3 text-sm text-emerald-700 dark:text-emerald-300"
          aria-live="polite"
        >
          {{ message }} You can now sign in with your new password.
        </p>
        <router-link class="btn mt-6 inline-flex" :to="{ name: 'login' }">
          Continue to sign in
        </router-link>
      </template>

      <template v-else-if="hasResetCredentials">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          Choose a new password
        </h1>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-300">
          This one-time link will stop working after your password is changed.
        </p>

        <form class="mt-6 space-y-4" @submit.prevent="submit">
          <div>
            <label
              for="reset-email"
              class="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
            >
              Email address
            </label>
            <input
              id="reset-email"
              v-model="email"
              type="email"
              autocomplete="email"
              readonly
              class="block w-full rounded-lg border border-gray-300 bg-gray-100 p-2.5 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            />
          </div>
          <div>
            <label
              for="new-password"
              class="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
            >
              New password
            </label>
            <input
              id="new-password"
              v-model="password"
              type="password"
              autocomplete="new-password"
              required
              class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label
              for="new-password-confirmation"
              class="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
            >
              Confirm new password
            </label>
            <input
              id="new-password-confirmation"
              v-model="passwordConfirmation"
              type="password"
              autocomplete="new-password"
              required
              class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Use 12 or more characters, including uppercase, lowercase, a number,
            and a symbol.
          </p>
          <p
            v-if="message"
            class="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300"
            aria-live="polite"
          >
            {{ message }}
          </p>
          <button type="submit" class="btn w-full" :disabled="submitting">
            {{ submitting ? "Resetting…" : "Reset password" }}
          </button>
        </form>
      </template>

      <template v-else>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          Invalid reset link
        </h1>
        <p class="mt-3 text-sm text-gray-500 dark:text-gray-300">
          This link is incomplete. Ask your administrator to send a new password
          reset email.
        </p>
        <router-link class="btn mt-6 inline-flex" :to="{ name: 'login' }">
          Back to sign in
        </router-link>
      </template>
    </div>
  </section>
</template>
